import React from 'react';
import { EditorView } from '@codemirror/view';
import { Extension } from '@codemirror/state';
import { EditorPanel } from '../editor/EditorPanel';
import { SearchPanel } from '../editor/SearchPanel';
import { LogLocation } from '../../types';
import { useEditorState, useUI, useLogs, useSettings } from '../../contexts';
import { useSearch } from '../../hooks/useSearch';

type EditorSectionProps = {
  dropProps: {
    onDragOver: (e: React.DragEvent) => void;
    onDrop: (e: React.DragEvent) => void;
  };
  dragHandleProps: {
    draggable: boolean;
    onDragStart: () => void;
    onDragEnd: () => void;
  };
  className: string;
  style: React.CSSProperties;
  editorExtensions: Extension[];
  isDirty: (path: string) => boolean;
  resolvePath: (id: string) => string;
  pendingLocation: LogLocation | null;
  editorViewRef: React.MutableRefObject<EditorView | null>;
  onSelectTab: (id: string) => void;
  onCloseTab: (id: string) => void;
  onChange: (value: string) => void;
  onBlur: () => void;
  onStartResize: (clientY: number) => void;
  onChangeIndent: (n: number) => void;
  onChangeEncoding: (val: string) => void;
  goToEditorLocation: (fileId: string, line: number, column: number) => void;
  setPendingLocation: (loc: LogLocation | null) => void;
  onBreadcrumbNavigate?: (path: string) => void;
};

export const EditorSection = React.memo(function EditorSection({
  dropProps,
  dragHandleProps,
  className,
  style,
  editorExtensions,
  isDirty,
  resolvePath,
  pendingLocation,
  editorViewRef,
  onSelectTab,
  onCloseTab,
  onChange,
  onBlur,
  onStartResize,
  onChangeIndent,
  onChangeEncoding,
  goToEditorLocation,
  setPendingLocation,
  onBreadcrumbNavigate,
}: EditorSectionProps) {
  const { activeFile, openTabs, code } = useEditorState();
  const { editorHeight, status } = useUI();
  const { lintSummary } = useLogs();
  const { theme, indentSize, encoding } = useSettings();
  const { replaceQuery } = useUI();

  const { findNext, findPrevious, replaceOne, replaceAll } = useSearch(editorViewRef);

  const handleReplace = () => replaceOne(replaceQuery);
  const handleReplaceAll = () => replaceAll(replaceQuery);

  return (
    <section
      {...dropProps}
      className={className}
      style={style}
      aria-label="Code editor"
    >
      <SearchPanel
        onFindNext={findNext}
        onFindPrevious={findPrevious}
        onReplace={handleReplace}
        onReplaceAll={handleReplaceAll}
      />
      <div className="panel-header">
        <div>
          <p className="eyebrow">Editor</p>
          <h2>Code</h2>
        </div>
        <div className="panel-header-actions">
          <div className="panel-drag-handle" {...dragHandleProps} title="Panelleri sürükle">
            ⠿
          </div>
        </div>
      </div>
      <EditorPanel
        activeFile={activeFile}
        openTabs={openTabs}
        code={code}
        editorHeight={editorHeight}
        status={status}
        lintSummary={lintSummary}
        indentSize={indentSize}
        encoding={encoding}
        editorExtensions={editorExtensions}
        theme={theme}
        isDirty={isDirty}
        resolvePath={resolvePath}
        onSelectTab={onSelectTab}
        onCloseTab={onCloseTab}
        onChange={onChange}
        onBlur={onBlur}
        onCreateEditor={(view) => {
          editorViewRef.current = view;
          if (pendingLocation && pendingLocation.fileId === activeFile) {
            goToEditorLocation(pendingLocation.fileId, pendingLocation.line, pendingLocation.column);
            setPendingLocation(null);
          }
        }}
        onStartResize={onStartResize}
        onChangeIndent={onChangeIndent}
        onChangeEncoding={onChangeEncoding}
        onBreadcrumbNavigate={onBreadcrumbNavigate}
      />
    </section>
  );
});
